/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { classNames, clone } from '../../../utils';
import { CheckCircleBlueIcon, CheckCircleYellowIcon, ChevronRightIcon, CloseIcon } from '../../../assets/icons';

export const HealthStatement = ({ onClose, questions }) => {
  const request = useSelector(state => state.request);
  const dispatch = useDispatch();
  const [form, setForm] = useState([]);

  const onToggle = (index) => {
    const beneficiaries = clone(form);
    beneficiaries[index].isActive = !beneficiaries[index].isActive;
    setForm(beneficiaries);
  };

  const onChange = (benefIdx, questionIdx, value) => {
    const beneficiaries = clone(form);
    beneficiaries[benefIdx].questions[questionIdx].answer_id = value;
    setForm(beneficiaries);
  };

  const isValidForm = (showError = true) => {
    const onError = (msg) => {
      if (showError) toast.error(msg);
      return false;
    };

    const isIncomplete = form.some(benef => {
      return Object.values(benef.questions || {}).some(question => !question.answer_id);
    });

    return isIncomplete
      ? onError('Debe completar todas las preguntas')
      : true;
  };

  const submit = () => {
    if (!isValidForm()) return;

    // Si alguna de las respuestas de los beneficiarios requiere análisis
    // se debe enviar la compra sin el pago para que sea revisada en Gestión de Suscripciones
    const toAnalize = form.some(b => {
      return b.questions.some(q => {
        return q.answers.some(x => x.id === q.answer_id && x.require_analysis);
      });
    });

    dispatch({
      type: 'SET_REQUEST',
      payload: {
        ...request,
        toAnalize,
        isDSRAnswered: true,
        beneficiaries: form,
      },
    });
    onClose();
  };

  useEffect(() => {
    // Verifica si todos los productos no requieran DSR
    const isAllProductsWithoutDSR = request?.products?.every(x => !x.product.apply_dsr);

    // Verifica si hay productos que no requieran DSR
    const hasProductsWithoutDSR = request?.products?.some(x => !x.product.apply_dsr);

    let beneficiaries = request?.beneficiaries ?? [];
    if (hasProductsWithoutDSR) {
      // Excluye el beneficiario que solo tiene productos que no requieran DSR
      beneficiaries = request?.beneficiaries?.filter(x => {
        // Obtiene los productos del beneficiario
        const benefProducts = request?.products?.filter(p => p.beneficiaries.some(b => +b.id === +x.id));
        // Verifica si alguno de los productos requiere el DSR
        return benefProducts.some(p => p.product.apply_dsr);
      }) || [];
    }

    const _form = beneficiaries?.map(benef => ({
      ...benef,
      isActive: false,
      questions: questions.map(x => ({ ...x, answer_id: null })),
    })) || [];

    setForm(_form);
    if (isAllProductsWithoutDSR || !beneficiaries?.length) submit();
  }, [request, questions]);

  const isSubmitEnabled = isValidForm(false);

  return (
    <div className="w-full lg:w-[34rem] bg-white rounded-xl overflow-hidden py-2 px-3 flex flex-col items-center">
      <div className="w-full">
        <button
          className="absolute top-3 right-3 bg-white"
          onClick={() => onClose()}
        >
          <img src={CloseIcon} className="w-4 h-4 object-contain" alt="" />
        </button>
        <p className="font-bold text-lg text-darkBlue">
          Datos de Declaración de Salud Resumida
        </p>
      </div>

      <div className="w-full flex-1 mt-5">
        {form.map((item, index) => (
          <BeneficiaryItem
            key={`beneficiary-${index}`}
            index={index}
            item={item}
            isActive={item.isActive}
            onChange={onChange}
            toggleSelection={() => onToggle(index)}
          />
        ))}
        <div className="w-full border-b border-b-gray6" />
      </div>

      <div className="mt-2">
        <button
          className={classNames(
            'rounded-lg w-60 text-center h-10 font-bold my-2 disabled:bg-gray6 disabled:text-gray3',
            isSubmitEnabled ? 'bg-yellow text-black' : 'text-gray bg-gray6'
          )}
          onClick={submit}
          disabled={!isSubmitEnabled}
        >
          <span>Continuar</span>
        </button>
      </div>
    </div>
  );
};

const BeneficiaryItem = ({ item, index: benefIdx, isActive, toggleSelection, onChange }) => {

  const isComplete = Object.values(item.questions).every(x => !!x.answer_id);

  return (
    <>
      <button
        className={classNames(
          'w-full flex justify-between items-center py-1 border-t border-t-gray6 border-b-gray6',
          isActive ? 'border-b' : 'bg-transparent'
        )}
        onClick={toggleSelection}
      >
        <div>
          {isComplete ? (
            <img
              src={CheckCircleYellowIcon}
              className={classNames(
                'w-5 h-5 object-contain',
                isComplete ? 'grayscale-0 opacity-100' : 'grayscale opacity-30'
              )}
              alt=""
            />
          ) : (
            <img
              src={CheckCircleBlueIcon}
              className="w-5 h-5 object-contain grayscale opacity-30"
              alt=""
            />
          )}
        </div>
        <div className="flex-1 text-left px-2">
          <p className="text-lg">
            { item.fullName }
          </p>
          <p className="text-sm font-semibold">
            { item.relationship }
          </p>
        </div>
        <div>
          <img
            src={ChevronRightIcon}
            className={classNames('w-4 h-4 object-contain', isActive ? '-rotate-90' : 'rotate-90')}
            alt=""
          />
        </div>
      </button>

      {isActive && (
        <div className="px-5 py-2">
          {item.questions.map((q, questionIdx) => (
            <div key={questionIdx} className="mb-5">
              <p className="font-semibold leading-5 text-text mb-2">
                { questionIdx + 1}. { q.question }
              </p>

              {q.answers.map((answer, answerIdx) => (
                <button
                  key={`disease-${answerIdx}`}
                  className="mb-2 flex items-center"
                  onClick={() => onChange(benefIdx, questionIdx, answer.id)}
                >
                  {q.answer_id === answer.id ? (
                    <img src={CheckCircleYellowIcon} className="w-4 h-4 mr-2 object-contain" alt="" />
                  ) : (
                    <div className="w-4 h-4 mr-2 aspect-square rounded-full border bg-gray5 border-gray6" />
                  )}
                  <span className="text-sm">{ answer.label }</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </>
  );
};
